import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { SetSendTokenDataAction } from 'src/app/angular-wallet-base/actions/gasStation.actions';
import {
  AGX_SERVICE_FEE,
  AUTH_REASON,
  AUX_SERVICE_FEE,
  EST_SYS_ASSET_NET_FEE,
  SERVICE_FEE_PERCENTAGE,
} from 'src/app/angular-wallet-base/constants';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { GasStationService, HealthResponse } from 'src/app/angular-wallet-base/services/gas-station.service';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { getSideMenuStatus } from 'src/app/angular-wallet-base/store/appSettings';
import { AppState, getWalletState } from 'src/app/angular-wallet-base/store/appState';
import { getServiceStatus } from 'src/app/angular-wallet-base/store/connection';
import { getBaseCurrency } from 'src/app/angular-wallet-base/store/userPreferences';
import { round } from 'src/app/angular-wallet-base/ts-util';
import { presentAlert } from 'src/app/angular-wallet-base/utils';
import { PinModalComponent } from 'src/app/components/pin-modal/pin-modal.component';
import { SelectAssetModal } from '../../select-asset-modal/select-asset-modal.component';
import { SelectNetworkModal } from './select-network-modal/select-network-modal';
import { SuccessMoveComponent } from './success-move-modal/success-move-modal';
import { TokenInfo, TokenInfoExtended } from '../../../global';
import { inspect } from 'util';
import { AuthService } from '../../../angular-wallet-base/services/auth.service';

@Component({
  selector: 'app-move',
  templateUrl: './move.component.html',
  styleUrls: ['./move.component.scss'],
})
export class MoveComponent implements OnInit, OnDestroy {
  @Output() stopLoading: EventEmitter<null> = new EventEmitter();

  public isDesktopMode: boolean;
  public assetToMove: TokenInfoExtended;
  public moveForm: FormGroup;
  public selectedNetwork;
  public fiatAmount: string;
  public serviceFee: number;
  public networkFee: string;
  public currentBaseCurrency: string;
  public inputValue: any;
  public moveEndpointHealth: HealthResponse;
  public maxMoveAmount: number;
  public showForm: boolean | undefined = undefined;
  public isMaxClicked: boolean = false;
  protected subscriptions = new Subscription();

  constructor(
    public appMode: AppModeService,
    protected store: Store<AppState>,
    private modalController: ModalController,
    private formBuilder: FormBuilder,
    private gasStationService: GasStationService,
    private nav: NavController,
    private authService: AuthService,
    private alertController: AlertController,
    private translate: TranslateService
  ) {
    this.appMode.isDesktopMode.subscribe((mode) => (this.isDesktopMode = mode));
    this.buildForm();
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async ngOnInit() {
    try {
      const res = await this.gasStationService.getAGXandAUXhealth();
      let services;
      this.subscriptions.add(
        this.store.select(getServiceStatus).subscribe((data) => {
          services = data;
          this.showFormAsPerTheAvailabilityOfServices(res, services);
          Logger.info('services gas', services);
        })
      );
      this.currentBaseCurrency = await this.store.select(getBaseCurrency).pipe(take(1)).toPromise();
      Logger.info('currentBaseCurrency ', this.currentBaseCurrency);
      this.networkFee = EST_SYS_ASSET_NET_FEE;
    } catch (error) {
      this.showForm = false;
      Logger.error('getAGXandAUXhealth', inspect(error));
    }

  }

  private showFormAsPerTheAvailabilityOfServices(res: HealthResponse, services: any) {
    // to stop loader of move segment in parent component
    this.stopLoading.emit();
    if ((res && Object.keys(res)?.length) || services?.sysBB) {
      this.moveEndpointHealth = res;
      this.showForm = true;
    } else {
      this.moveEndpointHealth = null;
      this.showForm = false;
    }
  }

  public buildForm() {
    this.moveForm = this.formBuilder.group({
      amount: ['', [Validators.required, Validators.min(1), this.checkMinimumAndMaximumQty()]],
      address: ['', [Validators.required]],
      assetGuid: ['', [Validators.required]],
      memo: ['', [Validators.required]],
    });
  }

  // check min and max of moving asset//
  checkMinimumAndMaximumQty = (): ValidatorFn => {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      const intValue = Number(c.value);

      if (!this.assetToMove) return null;

      if (+this.assetToMove.preciseBalance < 1) { // check whether the asset to be moved has enogh balance
        return { notEnoughAsset: true };
      }

      const symbol = this.assetToMove.symbol.toLowerCase();
      const healthAssetBalance = +this.moveEndpointHealth.avalanche[symbol].balance;

      this.maxMoveAmount = Math.min(
        this.assetToMove.preciseBalance,
        parseFloat(this.moveEndpointHealth?.avalanche?.[symbol]?.balance ?? '0')
      );

      const validateMin = () => isNaN(intValue) || intValue < 1;
      const validateMax = () => isNaN(intValue) || intValue > +this.assetToMove.preciseBalance;
      const validateMaxOfHealthAssetBalance = () => isNaN(intValue) || intValue > healthAssetBalance;

      if ((symbol === 'agx' || symbol === 'aux')) {
        if (validateMin()) return { minValue: true };
        if (validateMax()) return { maxValue: true };
        if (validateMaxOfHealthAssetBalance()) return { maxValueOfHealthAssetBalance: true };
      }
      return null;
    };
  }

 calculateMaxValue() {
      this.isMaxClicked = true;
      // console.log('thsihit', this.maxMoveAmount + 1)
      // this.moveForm.patchValue(
      //   { amount: this.maxMoveAmount },
      //   { emitEvent: true }
      // );
      this.onInputValueChange(this.maxMoveAmount - 0.01);
  }

 onInputValueChange(value) {
    this.moveForm.get('amount').setValue(+value);
    Logger.info('moving amount ', value, this.moveForm.get('amount').value);
    const { symbol, retailFiatValue, fiatValue } = this.assetToMove;
    this.fiatAmount = (Number(value) * (retailFiatValue || fiatValue)).toString();
    const serviceFee = +value * (SERVICE_FEE_PERCENTAGE / 100);
    if (symbol === 'AGX') {
      this.serviceFee = serviceFee > AGX_SERVICE_FEE ? serviceFee : AGX_SERVICE_FEE;
    } else if (symbol === 'AUX') {
      this.serviceFee = serviceFee > AUX_SERVICE_FEE ? serviceFee : AUX_SERVICE_FEE;
    }
  }

  async openSuccessModal(tranId: string) {
    const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();

    let cssClass = '';
    cssClass += this.isDesktopMode ? 'send-asset-modal-desktop' : 'send-asset-modal';
    cssClass += !sideMenuHidden && this.isDesktopMode ? ' with-side-menu' : '';
    const successModalData = {
      token_info: this.assetToMove,
      amount: this.moveForm.get('amount').value,
      from_network: this.assetToMove.baseChainSymbol,
      to_network: this.selectedNetwork.baseChainSymbol,
      tranId,
    };
    const successMoveModal = await this.modalController.create({
      component: SuccessMoveComponent,
      cssClass,
      componentProps: {
        successModalData,
      },
      id: 'successMoveModal',
    });
    await successMoveModal.present();
    await successMoveModal.onDidDismiss().then((res) => {
      Logger.info('successMoveModal', res);
      if (res.data?.backToHome) {
        this.nav.navigateRoot(['/home-page/established']);
      } else {
        this.buildForm();
        this.assetToMove = null;
        this.selectedNetwork = null;
        this.serviceFee = null;
        this.inputValue = null;
        this.stopLoading.emit();
      }
    });
  }

  async selectNetwork() {
    const cssClass = this.isDesktopMode ? 'select-asset-modal-desktop' : 'select-asset-modal';
    const modal = await this.modalController.create({
      component: SelectNetworkModal,
      cssClass,
      backdropDismiss: false,
      initialBreakpoint: this.isDesktopMode ? undefined : 0.9,
      breakpoints: this.isDesktopMode ? undefined : [0, 0.6, 0.9, 1],
      id: 'selectNetworkModal',
    });
    await modal.present();
    await modal.onDidDismiss().then(async (res) => {
      Logger.info('networkDetails ', res);
      if (res?.data) {
        this.selectedNetwork = res.data.networkInfo;
        const wallet = await this.store.select(getWalletState).pipe(take(1)).toPromise();
        const address = wallet.keychain['AVAX'].address;
        let symbol = "AVAX";
        if (symbol && address) {
          const result = await this.checkTreasuryBalanceOfSelectedNetwork(symbol);
          Logger.info('result ', symbol, +this.moveEndpointHealth.avalanche.treasury.balance);
          if (result) {
            this.showForm = false;
          } else {
            this.moveForm.get('memo').setValue(symbol + ':' + address);
          }
        }
      }
    });
  }

  // currently we have only Avalanche network for moving assets TODO(in the future, we may have multiple networks)
  async checkTreasuryBalanceOfSelectedNetwork(symbol) {
    switch (symbol) {
      case 'AVAX':
        return Number(this.moveEndpointHealth.avalanche.treasury.balance) < 0.1;

      default:
        return false;
    }
  }

  async selectAsset() {
    if (!this.moveEndpointHealth) {
      return;
    }
    const cssClass = this.isDesktopMode ? 'select-asset-modal-desktop' : 'select-asset-modal';
    const modal = await this.modalController.create({
      component: SelectAssetModal,
      componentProps: {
        actionType: 'move',
        assetHealthForMove: this.moveEndpointHealth,
      },
      cssClass,
      initialBreakpoint: this.isDesktopMode ? undefined : 0.9,
      breakpoints: this.isDesktopMode ? undefined : [0, 0.6, 0.9, 1],
      id: 'selectAssetModal',
    });
    await modal.present();
    await modal.onDidDismiss().then(async (res) => {
      Logger.info('tokenDetails ', res);
      if (res?.data) {
        this.assetToMove = res.data.tokenInfo;
        this.inputValue = null;
        this.fiatAmount = null;
        const canMoveAsset = await this.checkEnoughBalanceForTransaction(
          res.data.movingAssetNetworkBalance,
          this.assetToMove.baseChainSymbol
        );
        if (!canMoveAsset) {
          this.moveForm.get('assetGuid').setValue(this.assetToMove.assetGuid);
          this.moveForm.get('address').setValue(this.assetToMove.address);
        }
      }
    });
  }

  // currently we can only able to move  syscoin network assets TODO(in the future, we may can move assets in the other networks)
  async checkEnoughBalanceForTransaction(balance, symbol) {
    const SYS_NETWORK_LIMIT = Number(EST_SYS_ASSET_NET_FEE.split('-')[1]);
    switch (symbol) {
      case 'SYS':
        return Number(balance) < SYS_NETWORK_LIMIT;

      default:
        return false;
    }
  }

  async moveAsset() {
    try {
      Logger.info('formValue', this.moveForm.value);
    const symbol = this.assetToMove.symbol.toLowerCase();
    let curAssetGuid = this.moveForm.get('assetGuid').value;
    if (symbol == 'agx' && curAssetGuid != 367794646) {
      curAssetGuid = 367794646;
    }
    if (symbol == 'aux' && curAssetGuid != 1358717298) {
      curAssetGuid = 1358717298;
    }
    let moveAmount = Number(this.moveForm.get('amount').value);
    if (moveAmount < 0.1) {
      throw Error(`Wrong Move amount ${moveAmount}`);
    }
    let memo = this.moveForm.get('memo').value;
    if (!memo) {
      throw Error(`Wrong memo ${memo}`);
    }
    let toAddress = this.moveEndpointHealth.wallet.address;
    if (!toAddress) {
      throw Error(`wrong the move wallet ${toAddress}`);
    }
    
    this.store.dispatch(
      new SetSendTokenDataAction({
        amount: moveAmount,
        address: this.moveForm.get('address').value,
        assetGuid: curAssetGuid,
        memo: memo,
        type: 'MOVE',
        to: toAddress,
      })
    );

    const modal = await this.modalController.create({
      component: PinModalComponent,
      backdropDismiss: false,
      cssClass: 'fullscreen-modal',
      componentProps: {
        reason: AUTH_REASON.TRADE_MOVE_ASSET,
      },
      id: 'pinModal',
      });
      await modal.present();
      modal.onDidDismiss().then(async (res) => {
        if (res?.data?.txid) {
          Logger.info('tranId', res?.data);
          await this.openSuccessModal(res?.data?.txid);
        }
      });
    } catch (e) {
      Logger.error('first error', e);
      let message =  await this.translate.get('gas_station.move_error.error_description').toPromise();
      message += "before move " + e?.message;
      const alert = await this.alertController.create({
        header: await this.translate.get('gas_station.refill_confirmation.error_title').toPromise(),
        message: message,
      });
      await alert.present();
    }
    
  }
}
